'use client';

import React, { useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type ResponseState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'copilot'; message: string }
  | { status: 'anna'; message: string }
  | { status: 'rate_limited'; message: string }
  | { status: 'error'; message: string };

// ─── Constants ────────────────────────────────────────────────────────────────

const SITUATION_TEXT = `It's 8.47am. You have 34 unread emails. One of them is a thread that's been going back and forth between six people for three days and contains what you're fairly sure is an important decision, buried somewhere in the middle. You have a meeting at 9.

Old approach: scroll through the whole thing, try to piece it together, probably miss something.

New approach: ask Copilot.`;

const EMAIL_THREAD = `Subject: Q3 Budget Review — follow-up actions

Sarah Chen (Mon 09:14): Following up from last week's finance call. We need to finalise the Q3 numbers before end of week. Marketing, can you confirm your revised forecast?

James Whitfield (Mon 11:32): Marketing here. We're looking at an 8% reduction from October. I'll have the updated figures to Sarah by Wednesday.

Sarah Chen (Mon 14:05): Thanks James. Finance will circulate updated project codes by Wednesday too. No further headcount changes this quarter — that was confirmed on the call.

David Park (Tue 09:47): Quick note — I've booked a follow-up meeting for the week commencing 14th. Calendar invite going out today.

You (Tue 16:22): Just to confirm — my revised budget forecast is due Thursday 5pm?

Sarah Chen (Wed 08:55): Correct. Thursday 5pm for the revised forecast. Final figures confirmed by Finance by end of week. Thanks everyone.`;

const DEFAULT_PROMPT =
  'Summarise this email thread and list any decisions that were made and any actions I need to take, including who owns them.';

const SUPABASE_FUNCTION_URL_KEY = 'NEXT_PUBLIC_SUPABASE_URL';

// ─── LivePromptBlock ──────────────────────────────────────────────────────────

export default function LivePromptBlock() {
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [response, setResponse] = useState<ResponseState>({ status: 'idle' });
  const responseRef = useRef<HTMLDivElement>(null);

  const handleRun = async () => {
    if (!prompt.trim()) return;
    setResponse({ status: 'loading' });

    try {
      const supabase = createClient();
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
      const functionUrl = `${supabaseUrl}/functions/v1/live-prompt-lesson-1-2`;

      const res = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        },
        body: JSON.stringify({ userPrompt: prompt.trim(), lessonId: '1-2' }),
      });

      const data = await res.json();

      if (res.status === 429 || data.type === 'rate_limited') {
        setResponse({ status: 'rate_limited', message: data.message || "You've tried a lot of versions — nice work. Come back to this in a bit, or move on to Lesson 1.3." });
      } else if (!res.ok || data.error) {
        setResponse({ status: 'error', message: data.error || 'Something went wrong. Try again in a moment.' });
      } else if (data.type === 'anna') {
        setResponse({ status: 'anna', message: data.message });
      } else {
        setResponse({ status: 'copilot', message: data.message });
      }

      // Scroll to response
      setTimeout(() => {
        responseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch {
      setResponse({ status: 'error', message: 'Something went wrong. Try again in a moment.' });
    }
  };

  const handleReset = () => {
    setPrompt(DEFAULT_PROMPT);
    setResponse({ status: 'idle' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ border: '1px solid var(--border)' }}
      aria-label="Live Prompt — try it now"
    >
      {/* Header bar */}
      <div
        className="px-6 py-4 flex items-center gap-2"
        style={{ backgroundColor: 'var(--navy)' }}
      >
        {/* Copilot sparkle icon */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          style={{ color: 'var(--amber)', flexShrink: 0 }}
        >
          <path
            d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z"
            fill="currentColor"
          />
        </svg>
        <span
          className="font-body text-xs uppercase tracking-widest font-semibold"
          style={{ color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em' }}
        >
          Try It Now — Live
        </span>
      </div>

      <div className="p-6 flex flex-col gap-5" style={{ backgroundColor: '#F8F9FB' }}>
        {/* Situation / email thread */}
        <div>
          <p
            className="font-body text-xs uppercase tracking-widest font-semibold mb-3"
            style={{ color: 'var(--navy)', letterSpacing: '0.1em' }}
          >
            The email thread
          </p>
          <div
            className="rounded-lg p-4"
            style={{
              backgroundColor: '#EEF1F7',
              border: '1px solid #D4DBEA',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '0.75rem',
              color: '#374151',
              lineHeight: '1.75',
              whiteSpace: 'pre-line',
            }}
          >
            {EMAIL_THREAD}
          </div>
        </div>

        {/* Editable prompt */}
        <div>
          <label
            htmlFor="live-prompt-input"
            className="font-body text-xs uppercase tracking-widest font-semibold mb-2 block"
            style={{ color: 'var(--navy)', letterSpacing: '0.1em' }}
          >
            Your prompt
          </label>
          <textarea
            id="live-prompt-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            className="w-full rounded-lg px-4 py-3 font-body text-sm resize-none focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'white',
              border: '1px solid #D4DBEA',
              color: 'var(--navy)',
              lineHeight: '1.7',
              focusRingColor: 'var(--amber)',
            }}
            aria-label="Edit the prompt before running"
            placeholder="Type your prompt here…"
          />
          {/* Reset link */}
          {prompt !== DEFAULT_PROMPT && (
            <button
              onClick={handleReset}
              className="font-body text-xs mt-1.5 underline underline-offset-2 transition-opacity hover:opacity-70"
              style={{ color: 'var(--text-muted)' }}
              aria-label="Reset to original prompt"
            >
              Start over with the original prompt
            </button>
          )}
        </div>

        {/* Run button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleRun}
            disabled={response.status === 'loading' || !prompt.trim()}
            className="inline-flex items-center gap-2 font-body text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--amber)', color: 'white' }}
            aria-label="Run the prompt"
          >
            {response.status === 'loading' ? (
              <>
                <LoadingSpinner />
                Reading the thread…
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3 2l9 5-9 5V2z" fill="currentColor" />
                </svg>
                Run it
              </>
            )}
          </button>
          <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
            or ⌘↵
          </span>
        </div>

        {/* Response panel */}
        <div ref={responseRef}>
          {response.status === 'copilot' && (
            <CopilotPanel message={response.message} />
          )}
          {response.status === 'anna' && (
            <AnnaPanel message={response.message} />
          )}
          {response.status === 'rate_limited' && (
            <RateLimitPanel message={response.message} />
          )}
          {response.status === 'error' && (
            <ErrorPanel message={response.message} />
          )}
        </div>

        {/* Reset link when response is shown */}
        {['copilot', 'anna', 'rate_limited', 'error'].includes(response.status) && (
          <button
            onClick={handleReset}
            className="font-body text-xs self-start underline underline-offset-2 transition-opacity hover:opacity-70"
            style={{ color: 'var(--text-muted)' }}
            aria-label="Reset to original prompt and clear response"
          >
            Start over with the original prompt
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Copilot response panel ───────────────────────────────────────────────────

function CopilotPanel({ message }: { message: string }) {
  return (
    <div
      className="rounded-xl p-6"
      style={{ backgroundColor: 'var(--navy)', border: 'none' }}
      role="region"
      aria-label="Copilot's answer"
    >
      <div className="flex items-center gap-2 mb-4">
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          style={{ color: 'var(--amber)', flexShrink: 0 }}
        >
          <path
            d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z"
            fill="currentColor"
          />
        </svg>
        <span
          className="font-body text-xs uppercase tracking-widest font-semibold"
          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em' }}
        >
          Copilot&rsquo;s answer
        </span>
      </div>
      <p
        className="font-body text-sm leading-relaxed whitespace-pre-line"
        style={{ color: 'rgba(255,255,255,0.88)', lineHeight: '1.85' }}
      >
        {message}
      </p>
    </div>
  );
}

// ─── Anna panel ───────────────────────────────────────────────────────────────

function AnnaPanel({ message }: { message: string }) {
  return (
    <div
      className="rounded-xl p-6"
      style={{
        backgroundColor: '#FCF3E6',
        border: '1px solid #E8CB98',
      }}
      role="region"
      aria-label="Anna jumps in"
    >
      <div className="flex items-center gap-3 mb-4">
        {/* Anna avatar */}
        <div
          className="flex items-center justify-center rounded-full flex-shrink-0 font-body font-bold text-sm"
          style={{
            width: 32,
            height: 32,
            backgroundColor: 'var(--amber)',
            color: 'white',
          }}
          aria-label="Anna's avatar"
        >
          A
        </div>
        <span
          className="font-body text-xs uppercase tracking-widest font-semibold"
          style={{ color: 'var(--amber)', letterSpacing: '0.1em' }}
        >
          Anna jumps in
        </span>
      </div>
      <p
        className="font-body text-sm leading-relaxed"
        style={{ color: '#6B4C2A', lineHeight: '1.85' }}
      >
        {message}
      </p>
    </div>
  );
}

// ─── Rate limit panel ─────────────────────────────────────────────────────────

function RateLimitPanel({ message }: { message: string }) {
  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#FFF8EE', border: '1px solid #E8CB98' }}
      role="status"
      aria-live="polite"
    >
      <p className="font-body text-sm" style={{ color: '#6B4C2A', lineHeight: '1.75' }}>
        {message}
      </p>
    </div>
  );
}

// ─── Error panel ──────────────────────────────────────────────────────────────

function ErrorPanel({ message }: { message: string }) {
  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
      role="alert"
    >
      <p className="font-body text-sm" style={{ color: '#991B1B', lineHeight: '1.75' }}>
        {message}
      </p>
    </div>
  );
}

// ─── Loading spinner ──────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="7"
        cy="7"
        r="5.5"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="2"
      />
      <path
        d="M7 1.5A5.5 5.5 0 0112.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
